import React, { useState, useEffect } from 'react';
import { 
  RotateCcw, Shield, Check, X,
  Car, Truck, Bus, Bike, 
  Home, Building, Store, School,
  Plane, Train, Ship, 
  TreePine, Flower, Sun, Cloud,
  Heart, Star, Circle, Square,
  Phone, Camera, Laptop, Watch,
  Coffee, Pizza, Cake, Apple
} from 'lucide-react';
import { FaMotorcycle } from "react-icons/fa6";

const ReCAPTCHA = ({ onChange, theme = 'dark' }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [selectedIcons, setSelectedIcons] = useState([]);
  const [challengeIcons, setChallengeIcons] = useState([]);
  const [targetIcon, setTargetIcon] = useState(null);
  const [targetIconName, setTargetIconName] = useState('');
  const [attempts, setAttempts] = useState(0);

  // Icon sets with their display names
  const iconSets = {
    vehicles: {
      name: 'vehicles',
      displayName: 'vehicles',
      icons: [
        { component: Car, name: 'car' },
        { component: Truck, name: 'truck' },
        { component: Bus, name: 'bus' },
        { component: Bike, name: 'bike' },
        { component: Plane, name: 'plane' },
        { component: Train, name: 'train' },
        { component: Ship, name: 'ship' },
        { component: FaMotorcycle, name: 'motorcycle' }
      ]
    },
    buildings: {
      name: 'buildings',
      displayName: 'buildings',
      icons: [
        { component: Home, name: 'house' },
        { component: Building, name: 'building' },
        { component: Store, name: 'store' },
        { component: School, name: 'school' }
      ]
    },
    nature: {
      name: 'nature',
      displayName: 'nature items',
      icons: [
        { component: TreePine, name: 'tree' },
        { component: Flower, name: 'flower' },
        { component: Sun, name: 'sun' },
        { component: Cloud, name: 'cloud' }
      ]
    },
    shapes: {
      name: 'shapes',
      displayName: 'shapes',
      icons: [
        { component: Heart, name: 'heart' },
        { component: Star, name: 'star' },
        { component: Circle, name: 'circle' },
        { component: Square, name: 'square' }
      ]
    },
    tech: {
      name: 'tech',
      displayName: 'technology items',
      icons: [
        { component: Phone, name: 'phone' },
        { component: Camera, name: 'camera' },
        { component: Laptop, name: 'laptop' },
        { component: Watch, name: 'watch' }
      ]
    },
    food: {
      name: 'food',
      displayName: 'food items',
      icons: [
        { component: Coffee, name: 'coffee' },
        { component: Pizza, name: 'pizza' },
        { component: Cake, name: 'cake' },
        { component: Apple, name: 'apple' }
      ]
    }
  };

  const iconSetKeys = Object.keys(iconSets);

  useEffect(() => {
    if (showChallenge) {
      generateChallenge();
    }
  }, [showChallenge]);

  const generateChallenge = () => {
    // Select random icon set
    const randomSetKey = iconSetKeys[Math.floor(Math.random() * iconSetKeys.length)];
    const selectedSet = iconSets[randomSetKey];
    
    // Select target icon from the set
    const targetIconObj = selectedSet.icons[Math.floor(Math.random() * selectedSet.icons.length)];
    setTargetIcon(targetIconObj.component);
    setTargetIconName(targetIconObj.name);

    // Create challenge grid with mix of icons
    const challengeGrid = [];
    const correctCount = Math.floor(Math.random() * 3) + 2; // 2-4 correct icons
    
    // Add correct icons
    for (let i = 0; i < correctCount; i++) {
      challengeGrid.push({
        component: targetIconObj.component,
        name: targetIconObj.name,
        isCorrect: true,
        id: `correct-${i}`
      });
    }

    // Add random incorrect icons from other sets
    const otherIcons = [];
    iconSetKeys.forEach(setKey => {
      if (setKey !== randomSetKey) {
        otherIcons.push(...iconSets[setKey].icons);
      }
    });

    // Fill remaining slots with incorrect icons
    while (challengeGrid.length < 9) {
      const randomIcon = otherIcons[Math.floor(Math.random() * otherIcons.length)];
      challengeGrid.push({
        component: randomIcon.component,
        name: randomIcon.name,
        isCorrect: false,
        id: `incorrect-${challengeGrid.length}`
      });
    }

    // Shuffle the grid
    const shuffledGrid = challengeGrid.sort(() => Math.random() - 0.5);
    setChallengeIcons(shuffledGrid);
    setSelectedIcons([]);
  };

  const handleInitialClick = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      setIsLoading(false);
      // 20% chance to verify immediately, 80% chance to show challenge
      if (Math.random() < 0.2) {
        setIsVerified(true);
        onChange && onChange('verified-token-' + Date.now());
      } else {
        setShowChallenge(true);
      }
    }, 1200);
  };

  const handleIconClick = (iconId) => {
    if (selectedIcons.includes(iconId)) {
      setSelectedIcons(selectedIcons.filter(id => id !== iconId));
    } else {
      setSelectedIcons([...selectedIcons, iconId]);
    }
  };

  const handleVerify = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      const correctIconIds = challengeIcons
        .filter(icon => icon.isCorrect)
        .map(icon => icon.id);
      
      const isCorrect = 
        selectedIcons.length === correctIconIds.length &&
        selectedIcons.every(id => correctIconIds.includes(id)) &&
        selectedIcons.length > 0;

      if (isCorrect) {
        setIsVerified(true);
        setShowChallenge(false);
        setIsLoading(false);
        onChange && onChange('verified-token-' + Date.now());
      } else {
        setIsLoading(false);
        setAttempts(prev => prev + 1);
        // Generate new challenge on failure
        setTimeout(() => {
          generateChallenge();
        }, 500);
      }
    }, 800);
  };

  const handleRefresh = () => {
    generateChallenge();
  };

  const reset = () => {
    setIsVerified(false);
    setShowChallenge(false);
    setChallengeIcons([]);
    setSelectedIcons([]);
    setIsLoading(false);
    setTargetIcon(null);
    setTargetIconName('');
    setAttempts(0);
    onChange && onChange('');
  };

  const themeClasses = theme === 'dark' 
    ? 'bg-gray-800 border-gray-600 text-white'
    : 'bg-white border-gray-300 text-gray-900';

  const buttonTheme = theme === 'dark'
    ? 'bg-blue-600 hover:bg-blue-700 text-white'
    : 'bg-blue-500 hover:bg-blue-600 text-white';

  if (showChallenge && challengeIcons.length > 0) {
    const TargetIconComponent = targetIcon;
    
    return (
      <div className={`w-full max-w-sm mx-auto border-2 rounded-lg overflow-hidden shadow-xl ${themeClasses}`}>
        {/* Challenge Header */}
        <div className="p-4 border-b border-gray-600">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium">reCAPTCHA</span>
            </div>
            <button
              onClick={handleRefresh}
              className="p-1 hover:bg-gray-700 rounded transition-colors"
              disabled={isLoading}
            >
              <RotateCcw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Target Icon Display */}
          <div className="flex items-center gap-3 mb-3 p-3 bg-gray-700 rounded-lg">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-500/20 rounded-lg border border-blue-500/30">
              {TargetIconComponent && <TargetIconComponent className="w-6 h-6 text-blue-400" />}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-200">
                Select all <span className="text-blue-400">{targetIconName}</span> icons
              </p>
              <p className="text-xs text-gray-400">Click on matching icons below</p>
            </div>
          </div>
        </div>

        {/* Icon Grid */}
        <div className="p-4">
          <div className="grid grid-cols-3 gap-3 mb-4">
            {challengeIcons.map((iconObj, index) => {
              const IconComponent = iconObj.component;
              const isSelected = selectedIcons.includes(iconObj.id);
              
              return (
                <div
                  key={iconObj.id}
                  onClick={() => handleIconClick(iconObj.id)}
                  className={`
                    aspect-square border-2 cursor-pointer rounded-lg transition-all
                    flex items-center justify-center relative overflow-hidden
                    hover:scale-105 transform duration-200
                    ${isSelected
                      ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/25'
                      : 'border-gray-600 bg-gray-700 hover:border-gray-500 hover:bg-gray-600'
                    }
                  `}
                >
                  <IconComponent className={`w-8 h-8 transition-colors ${
                    isSelected ? 'text-blue-300' : 'text-gray-300'
                  }`} />
                  
                  {isSelected && (
                    <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={reset}
              className="flex-1 py-2 px-4 border border-gray-600 rounded hover:bg-gray-700 transition-colors text-sm flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={selectedIcons.length === 0 || isLoading}
              className={`
                flex-1 py-2 px-4 rounded transition-colors text-sm font-medium flex items-center justify-center gap-2
                ${selectedIcons.length > 0 && !isLoading
                  ? buttonTheme
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Verifying...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Verify
                </>
              )}
            </button>
          </div>

          {attempts > 0 && (
            <div className="mt-3 text-xs text-yellow-400 text-center">
              Try again - Select all {targetIconName} icons
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-600 text-xs text-gray-500 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-3 h-3" />
            <span>Privacy - Terms</span>
          </div>
          <span className="text-gray-600">reCAPTCHA</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${themeClasses} min-w-[300px]`}>
      {/* Checkbox */}
      <div className="flex-shrink-0">
        {isVerified ? (
          <div className="w-7 h-7 bg-green-500 rounded border-2 border-green-500 flex items-center justify-center shadow-lg">
            <Check className="w-5 h-5 text-white" />
          </div>
        ) : (
          <button
            onClick={handleInitialClick}
            disabled={isLoading}
            className={`
              w-7 h-7 border-2 rounded transition-all hover:scale-105
              ${isLoading 
                ? 'border-blue-500 bg-blue-500/20' 
                : 'border-gray-400 hover:border-gray-300 bg-transparent hover:bg-gray-700'
              }
            `}
          >
            {isLoading && (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </button>
        )}
      </div>

      {/* Text and Status */}
      <div className="flex-1 flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {isVerified ? 'Verification complete!' : 'I\'m not a robot'}
          </span>
          {isVerified && (
            <span className="text-xs text-green-400 mt-1">✓ Human verified</span>
          )}
        </div>
        
        <div className="flex flex-col items-center text-xs text-gray-500">
          <div className="flex items-center gap-1 mb-1">
            <Shield className="w-6 h-6 text-blue-500" />
          </div>
          <span className="font-medium">reCAPTCHA</span>
        </div>
      </div>
    </div>
  );
};

export default ReCAPTCHA;
